import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import styles from './job-chat-box.module.css';

const getSocketURL = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://server-production-a1b2.up.railway.app/';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const socket = io(getSocketURL(), { 
  transports: ['websocket', 'polling'],
  withCredentials: true // Important for CORS with credentials
});


const JobChatBox = ({ jobTitle, jobId, applicantIds, onClose }) => {
    const [message, setMessage] = useState('');
    const [roomId, setRoomId] = useState('');
    const [senderId, setSenderId] = useState('');
    const [userId, setUserId] = useState('');
    const [messages, setMessages] = useState([]);
    const [recentMessage, setRecentMessage] = useState('');
    const [reloadMessage, setReloadMessage] = useState('');
    const [showOptions, setShowOptions] = useState(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        const fetchRoomId = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.post('/api/recruiter-to-applicants-message/start-chat', {
                    jobId,
                    applicantIds // Pass all applicant IDs
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                setRoomId(response.data.room._id);
                setSenderId(response.data.userId);
                fetchMessages(response.data.room._id);
                // console.log("jobTitle:be1:", jobTitle);
                // console.log("jobId:be1:", jobId);
                // console.log("applicantIds:be1:", applicantIds);
                
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'An error occurred';
                console.error(errorMessage);
            }
        };

        fetchRoomId();
    }, [jobId, applicantIds]);

    useEffect(() => {
        if (!roomId) return;

        socket.emit('joinRoom', { roomId });

        socket.on('receiveMessage', (newMessage) => {
            setReloadMessage(newMessage);
            setMessages((prevMessages) => [...prevMessages, newMessage]);
        });

        return () => {
            socket.off('receiveMessage');
            if (roomId) {
                socket.emit('leaveRoom', { roomId });
            }
        };
    }, [roomId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const fetchMessages = async (roomId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/recruiter-to-applicants-message/get-messages', {
                recruiterRoomId: roomId,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.data.success) {
                const filteredMessages = response.data.messages.filter(msg => !msg.deletedFor.includes(response.data.userId));
                setMessages(filteredMessages);
                setUserId(response.data.userId);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error fetching messages:jcb3';
            console.error(errorMessage);
        }
    };

    const handleSendMessage = async () => {
        try {
            const token = localStorage.getItem('token');
            const recruiterId = localStorage.getItem('recruiterId'); // Assuming recruiterId is stored in localStorage

            // Send message to all applicants
            const response = await axios.post('/api/recruiter-to-applicants-message/send-message', {
                recruiterRoomId: roomId,
                senderId: recruiterId,
                receiverIds: applicantIds, // Pass all applicant IDs
                message
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                const newMessage = response.data.newMessage;
                socket.emit('sendMessage', newMessage);
                setMessages((prevMessages) => [...prevMessages, newMessage]);
            }

            setMessage('');
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error sending messages to all applicants:jcb3';
            console.error(errorMessage);
        }
    };

    const handleMarkRead = async (messageId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/recruiter-to-applicants-message/mark-read', {
                messageId,
                userId: senderId
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setMessages((prevMessages) => prevMessages.map(msg => {
                if (msg._id === messageId) {
                    return { ...msg, readBy: [...msg.readBy, { userId: userId, readAt: new Date() }] };
                }
                return msg;
            }));

        } catch (error) {
            console.error('Error marking message as read:', error.message);
        }
    };

    const handleDeleteMessageForMe = async (messageId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/recruiter-to-applicants-message/delete-message-for-me', {
                messageId,
                userId: senderId
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setMessages((prevMessages) => prevMessages.filter(msg => msg._id !== messageId));
        } catch (error) {
            console.error('Error deleting message for me:', error.message);
        }
    };

    const handleDeleteMessageForAll = async (messageId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/recruiter-to-applicants-message/delete-message-for-all', {
                messageId
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setMessages((prevMessages) => prevMessages.filter(msg => msg._id !== messageId));
        } catch (error) {
            console.error('Error deleting message for all:', error.message);
        }
    };

    const handleClickOutside = (event) => {
        if (!event.target.closest(`.${styles.messageOptionsButton}`) && !event.target.closest(`.${styles.messageOptionsMenu}`)) {
            setShowOptions(null);
        }
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const now = new Date();
        const isThisYear = d.getFullYear() === now.getFullYear();
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear().toString().slice(-2);
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        return isThisYear ? `${hours}:${minutes} ${day}/${month}` : `${hours}:${minutes} ${day}/${month}/${year}`;
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.chatBox}>
            <div className={styles.chatHeader}>
                <h3>Send Message to All Applicants for {jobTitle}</h3>
                <button onClick={onClose}>Close</button>
            </div>
            <div className={styles.messagesContainer}>
                {messages.map((msg, index) => (
                    <div key={msg._id} className={`${styles.message} ${msg.senderId === userId ? styles.sent : styles.received}`}>
                        <div className={styles.messageContent}>
                            {msg.message}
                            <button className={styles.messageOptionsButton} onClick={() => setShowOptions(showOptions === index ? null : index)}>⋮</button>
                            {showOptions === index && (
                                <div className={styles.messageOptionsMenu}>
                                    <button className={styles.messageOptionsMenuButton} onClick={() => handleMarkRead(msg._id)}>Read by</button>
                                    <button className={styles.messageOptionsMenuButton} onClick={() => handleDeleteMessageForMe(msg._id)}>Delete for me</button>
                                    {msg.senderId === userId && (
                                        <button className={styles.messageOptionsMenuButton} onClick={() => handleDeleteMessageForAll(msg._id)}>Delete for everyone</button>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={styles.messageTimestamp}>
                            {formatDate(msg.createdAt)}
                        </div>
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>
            <textarea
                className={styles.chatTextarea}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
            />
            <button className={styles.sendButton} onClick={handleSendMessage}>Send</button>
        </div>
    );
};

export default JobChatBox;